import { useQuery } from '@tanstack/react-query';

import { supabase } from '../lib/supabase';
import { useCurrentCompany } from './useCurrentCompany';

export interface SocialOverviewStats {
  totalPosts: number;
  totalComments: number;
  totalReactions: number;
  pendingReports: number;
  activePosters: number;
}

export interface PostWithEngagement {
  id: string;
  content: string;
  media_urls: any;
  created_at: string;
  is_pinned: boolean;
  is_company_wide: boolean;
  comments_count: number;
  user_id: string;
  author: {
    id: string;
    first_name: string;
    last_name: string;
    avatar_url?: string;
  };
}

export interface TopPoster {
  user_id: string;
  first_name: string;
  last_name: string;
  avatar_url?: string;
  post_count: number;
}

/**
 * Fetch overview stats for the social dashboard
 */
export function useSocialOverview() {
  const { company } = useCurrentCompany();

  return useQuery({
    queryKey: ['social', 'overview', company?.id],
    enabled: !!company?.id,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<SocialOverviewStats> => {
      const companyId = company!.id;

      // Fetch post IDs once for reuse across queries
      const postsIdResult = await supabase
        .from('posts')
        .select('id, user_id')
        .eq('company_id', companyId)
        .eq('visible', true);

      const postRows = postsIdResult.data || [];
      const postIds = postRows.map((p: any) => p.id);
      const uniquePosters = new Set(postRows.map((p: any) => p.user_id));

      // Short-circuit dependent queries when there are no posts
      if (postIds.length === 0) {
        return {
          totalPosts: 0,
          totalComments: 0,
          totalReactions: 0,
          pendingReports: 0,
          activePosters: 0,
        };
      }

      // Run remaining queries in parallel
      const [commentsResult, reactionsResult, reportsResult] = await Promise.all([
        supabase
          .from('comments')
          .select('id', { count: 'exact', head: true })
          .in('post_id', postIds),
        supabase
          .from('reactions')
          .select('id', { count: 'exact', head: true })
          .in('post_id', postIds),
        supabase
          .from('post_reports')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'pending')
          .in('post_id', postIds),
      ]);

      return {
        totalPosts: postIds.length,
        totalComments: commentsResult.count || 0,
        totalReactions: reactionsResult.count || 0,
        pendingReports: reportsResult.count || 0,
        activePosters: uniquePosters.size,
      };
    },
  });
}

/**
 * Fetch recent posts with engagement data for the dashboard
 */
export function useRecentPosts(limit = 20) {
  const { company } = useCurrentCompany();

  return useQuery({
    queryKey: ['social', 'recent-posts', company?.id, limit],
    enabled: !!company?.id,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<PostWithEngagement[]> => {
      const { data, error } = await supabase
        .from('posts')
        .select(`
          id, content, media_urls, created_at, is_pinned, is_company_wide, user_id,
          author:user_profiles!inner(id, first_name, last_name, avatar_url)
        `)
        .eq('company_id', company!.id)
        .eq('visible', true)
        .order('is_pinned', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      // comments_count is not on the posts table; default to 0 for now
      return (data || []).map((p: any) => ({ ...p, comments_count: p.comments_count ?? 0 })) as PostWithEngagement[];
    },
  });
}

/**
 * Fetch top posters by post count
 */
export function useTopPosters(limit = 10) {
  const { company } = useCurrentCompany();

  return useQuery({
    queryKey: ['social', 'top-posters', company?.id, limit],
    enabled: !!company?.id,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<TopPoster[]> => {
      const { data, error } = await supabase
        .from('posts')
        .select('user_id, author:user_profiles!inner(id, first_name, last_name, avatar_url)')
        .eq('company_id', company!.id)
        .eq('visible', true);

      if (error) throw error;

      // Aggregate by user
      const counts: Record<string, TopPoster> = {};
      (data || []).forEach((post: any) => {
        const uid = post.user_id;
        if (!counts[uid]) {
          counts[uid] = {
            user_id: uid,
            first_name: post.author?.first_name || '',
            last_name: post.author?.last_name || '',
            avatar_url: post.author?.avatar_url,
            post_count: 0,
          };
        }
        counts[uid].post_count++;
      });

      return Object.values(counts)
        .sort((a, b) => b.post_count - a.post_count)
        .slice(0, limit);
    },
  });
}

/**
 * Fetch monthly post count trend (last 6 months)
 */
export function usePostTrend() {
  const { company } = useCurrentCompany();

  return useQuery({
    queryKey: ['social', 'post-trend', company?.id],
    enabled: !!company?.id,
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<{ month: string; count: number }[]> => {
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

      const { data, error } = await supabase
        .from('posts')
        .select('created_at')
        .eq('company_id', company!.id)
        .eq('visible', true)
        .gte('created_at', sixMonthsAgo.toISOString())
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Group by month
      const months: Record<string, number> = {};
      (data || []).forEach((post: any) => {
        const date = new Date(post.created_at);
        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        months[key] = (months[key] || 0) + 1;
      });

      // Fill in missing months
      const result: { month: string; count: number }[] = [];
      for (let i = 5; i >= 0; i--) {
        const d = new Date();
        d.setMonth(d.getMonth() - i);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        const label = d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        result.push({ month: label, count: months[key] || 0 });
      }

      return result;
    },
  });
}
